import { useRef, useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { createPortal } from 'react-dom'
import { ChevronDown, ListFilter } from 'lucide-react-native'
import { colors } from '@chillout/core'

export interface FilterGroup {
  id: string
  label: string
  options: { label: string; value: string }[]
  multiSelect?: boolean
}

interface AdminFilterDropdownProps {
  groups: FilterGroup[]
  activeFilters: Record<string, string | string[] | undefined>
  onChange: (id: string, value: string | string[] | undefined) => void
  filterLabel: string
  clearLabel: string
  disabled?: boolean
}

interface PanelPosition {
  top: number
  right: number
}

function countActive(activeFilters: Record<string, string | string[] | undefined>): number {
  let count = 0
  for (const val of Object.values(activeFilters)) {
    if (Array.isArray(val)) {
      count += val.length
    } else if (val) {
      count += 1
    }
  }
  return count
}

export function AdminFilterDropdown({
  groups,
  activeFilters,
  onChange,
  filterLabel,
  clearLabel,
  disabled = false,
}: AdminFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const buttonRef = useRef<View>(null)

  const activeCount = countActive(activeFilters)
  const hasActiveFilters = activeCount > 0

  function handleToggle() {
    if (disabled) return
    if (!isOpen && buttonRef.current) {
      const el = buttonRef.current as unknown as HTMLElement
      const rect = el.getBoundingClientRect()
      const right = window.innerWidth - rect.right
      const maxRight = window.innerWidth - 240 - 8
      setPanelPos({ top: rect.bottom + 4, right: Math.min(right, maxRight) })
    }
    setIsOpen((v) => !v)
  }

  function handleOptionPress(group: FilterGroup, value: string) {
    if (group.multiSelect) {
      const current = activeFilters[group.id]
      const arr = Array.isArray(current) ? current : current ? [current] : []
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value]
      onChange(group.id, next.length > 0 ? next : undefined)
    } else {
      onChange(group.id, activeFilters[group.id] === value ? undefined : value)
    }
  }

  function isOptionActive(groupId: string, value: string): boolean {
    const current = activeFilters[groupId]
    if (Array.isArray(current)) return current.includes(value)
    return current === value
  }

  function clearAll() {
    groups.forEach((g) => onChange(g.id, undefined))
  }

  const portal =
    isOpen && panelPos
      ? createPortal(
          <>
            <Pressable
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onPress={() => setIsOpen(false)}
              accessibilityLabel="Close filter"
            />
            <View
              className="fixed overflow-hidden rounded-lg border border-border bg-canvas-card"
              style={{
                top: panelPos.top,
                right: panelPos.right,
                width: 240,
                maxHeight: `calc(100vh - ${panelPos.top + 8}px)`,
                zIndex: 9999,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              } as object}
            >
              <ScrollView bounces={false}>
                {groups.map((group, gi) => (
                  <View key={group.id} className={gi > 0 ? 'border-t border-border' : ''}>
                    <Text className="px-4 pb-1.5 pt-3 text-xs font-semibold uppercase tracking-[0.8px] text-secondary">
                      {group.label}
                    </Text>
                    {group.options.map((option) => {
                      const isActive = isOptionActive(group.id, option.value)
                      return (
                        <Pressable
                          key={option.value}
                          onPress={() => handleOptionPress(group, option.value)}
                          accessibilityRole={group.multiSelect ? 'checkbox' : 'radio'}
                          accessibilityState={{ checked: isActive }}
                          accessibilityLabel={option.label}
                          className={`flex-row items-center gap-2.5 px-4 py-2.5 ${isActive ? 'bg-accent/5' : ''}`}
                        >
                          {group.multiSelect ? (
                            <View
                              className={`h-[14px] w-[14px] items-center justify-center rounded ${
                                isActive ? 'border-accent bg-accent' : 'border-2 border-border'
                              }`}
                            >
                              {isActive && (
                                <Text className="text-[10px] font-bold leading-[14px] text-white">✓</Text>
                              )}
                            </View>
                          ) : (
                            <View
                              className={`h-[14px] w-[14px] items-center justify-center rounded-full border-2 ${
                                isActive ? 'border-accent' : 'border-border'
                              }`}
                            >
                              {isActive && <View className="h-[6px] w-[6px] rounded-full bg-accent" />}
                            </View>
                          )}
                          <Text
                            className={`text-sm ${isActive ? 'font-semibold text-primary' : 'font-normal text-primary'}`}
                          >
                            {option.label}
                          </Text>
                        </Pressable>
                      )
                    })}
                  </View>
                ))}
                {hasActiveFilters && (
                  <View className="border-t border-border px-4 py-3">
                    <Pressable
                      onPress={clearAll}
                      accessibilityRole="button"
                      accessibilityLabel={clearLabel}
                    >
                      <Text className="text-sm font-medium text-accent">{clearLabel}</Text>
                    </Pressable>
                  </View>
                )}
              </ScrollView>
            </View>
          </>,
          document.body,
        )
      : null

  return (
    <View>
      <Pressable
        ref={buttonRef}
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={filterLabel}
        accessibilityState={{ disabled }}
        className={`flex-row items-center gap-1.5 rounded-md border px-3 py-2 ${
          hasActiveFilters ? 'border-accent bg-accent/5' : 'border-border bg-canvas-card'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <ListFilter
          size={14}
          color={hasActiveFilters ? colors.accent.DEFAULT : colors.text.primary}
          strokeWidth={2}
        />
        <Text className={`text-sm font-medium ${hasActiveFilters ? 'text-accent' : 'text-primary'}`}>
          {hasActiveFilters ? `${filterLabel} · ${activeCount}` : filterLabel}
        </Text>
        <View style={{ transform: [{ rotate: isOpen ? '180deg' : '0deg' }] }}>
          <ChevronDown
            size={14}
            color={hasActiveFilters ? colors.accent.DEFAULT : colors.text.primary}
            strokeWidth={2}
          />
        </View>
      </Pressable>
      {portal}
    </View>
  )
}